/**
 * Operation Management Module - Query Services
 *
 * GET hooks for MaintenanceTasks endpoints.
 */

import { useQuery } from '@tanstack/react-query';
import { useApi } from '../../../services/useApi';
import { MAINTENANCE_TASKS_LIST_URL, MAINTENANCE_TASK_DETAIL_URL } from '../constants';
import type {
  IApiResponse,
  IMaintenanceTaskListResult,
  IMaintenanceTaskListParams,
  IMaintenanceTaskDetailResult,
} from '../types';

/**
 * Fetches paginated list of maintenance tasks.
 * GET /api/TaskManagement/MaintenanceTasks
 * Response: { result: { tasks: [...], totalCount, page, pageSize } }
 */
export const useGetMaintenanceTasks = (params: IMaintenanceTaskListParams) => {
  const { apiPrivate } = useApi();

  return useQuery<IApiResponse<IMaintenanceTaskListResult>>({
    queryKey: [MAINTENANCE_TASKS_LIST_URL, params.page, params.pageSize, params.search, params.taskType, params.taskStatus, params.priority],
    queryFn: async () => {
      const queryParams: Record<string, string | number> = {
        page: params.page,
        pageSize: params.pageSize,
      };
      if (params.search) queryParams.search = params.search;
      if (params.taskType) queryParams.taskType = params.taskType;
      if (params.taskStatus) queryParams.taskStatus = params.taskStatus;
      if (params.priority) queryParams.priority = params.priority;

      const { data } = await apiPrivate.get(MAINTENANCE_TASKS_LIST_URL, { params: queryParams });
      return data;
    },
  });
};

/**
 * Fetches a single maintenance task by local integer id.
 * GET /api/MaintenanceTasks/{taskId}
 */
export const useGetMaintenanceTask = (taskId: number | undefined) => {
  const { apiPrivate } = useApi();

  return useQuery<IApiResponse<IMaintenanceTaskDetailResult>>({
    queryKey: [MAINTENANCE_TASK_DETAIL_URL, taskId],
    enabled: !!taskId,
    staleTime: 0,
    queryFn: async () => {
      const { data } = await apiPrivate.get(`${MAINTENANCE_TASK_DETAIL_URL}/${taskId}`);
      return data;
    },
  });
};
